/* AI image label injector
 * Reads the manifest rendered by snippets/ai-image-labels.liquid and adds a
 * small "KI" badge to any <img> whose (normalized) URL matches an entry.
 *
 * Managed content lives in the Shopify metaobject `ai_image_label` and is
 * matched by CDN path (query & size suffixes stripped), so the same image
 * gets labeled everywhere it appears (blog RTE, sections, cards, ...).
 */
(function () {
  'use strict';

  const manifestEl = document.querySelector('script[data-ai-label-manifest]');
  if (!manifestEl) return;

  let entries;
  try {
    entries = new Map(Object.entries(JSON.parse(manifestEl.textContent || '{}')));
  } catch (e) {
    return;
  }
  if (entries.size === 0) return;

  // Strip Shopify legacy size suffixes like _600x, _600x400, _600x400_crop_center
  // that appear before the file extension.
  const SIZE_SUFFIX_RE = /_(\d+x\d*|\d*x\d+)(_crop_[a-z]+)?(?=\.\w+$)/;

  function normalize(src) {
    if (!src) return '';
    try {
      const u = new URL(src, location.href);
      const path = decodeURIComponent(u.pathname).toLowerCase();
      return path.replace(SIZE_SUFFIX_RE, '');
    } catch (e) {
      return '';
    }
  }

  const manifest = new Map();
  for (let [image, ai_type] of entries) {
    if (!image) continue;

    const key = normalize(image);

    if (key) manifest.set(key, ai_type);
  }
  if (manifest.size === 0) return;

  function fileNameFromPath(path) {
    if (!path) return '';
    const i = path.lastIndexOf('/');
    return i === -1 ? path : path.slice(i + 1);
  }

  function fileNameWithoutExtension(path) {
    const name = fileNameFromPath(path);
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(0, i) : name;
  }

  const manifestByFilename = new Map();
  for (let [path, type] of manifest) {
    const name = fileNameFromPath(path);
    if (!name) continue;
    for (const filename of [name, fileNameWithoutExtension(name)]) {
      if (!manifestByFilename.has(filename)) {
        manifestByFilename.set(filename, type);
      } else if (manifestByFilename.get(filename) !== type) {
        // filename-only fallback is unsafe if multiple labels share a filename.
        manifestByFilename.set(filename, '');
      }
    }
  }

  const LABELS = {
    ai_generated: 'Mit KI erstellt',
    ai_modified: 'Mit KI bearbeitet'
  };

  // Icon: simple "KI" glyph. Kept as text to avoid SVG bloat.
  function buildBadge(type) {
    const text = LABELS[type] || LABELS.ai_generated;
    const badge = document.createElement('span');
    badge.className = 'ai-label-badge';
    badge.setAttribute('role', 'img');
    badge.setAttribute('tabindex', '0');
    badge.textContent = text;

    // Prevent the badge from triggering a wrapping <a> and toggle tooltip on tap.
    return badge;
  }

  const processed = new WeakSet();

  function candidatesFor(img) {
    const list = [];
    const seen = new Set();

    function push(kind, value) {
      if (!value) return;
      const id = kind + ':' + value;
      if (seen.has(id)) return;
      seen.add(id);
      list.push({ kind: kind, value: value });
    }

    function addPathCandidates(src) {
      const key = normalize(src);
      if (!key) return;
      push('path', key);
      if (key.indexOf('/cdn/shop/files/') === 0) {
        push('path', key.replace('/cdn/shop/files/', '/cdn/shop/articles/'));
        push('path', key.replace('/cdn/shop/files/', '/cdn/shop/article/'));
      } else if (key.indexOf('/cdn/shop/articles/') === 0) {
        push('path', key.replace('/cdn/shop/articles/', '/cdn/shop/files/'));
        push('path', key.replace('/cdn/shop/articles/', '/cdn/shop/article/'));
      } else if (key.indexOf('/cdn/shop/article/') === 0) {
        push('path', key.replace('/cdn/shop/article/', '/cdn/shop/files/'));
        push('path', key.replace('/cdn/shop/article/', '/cdn/shop/articles/'));
      }
      push('filename', fileNameFromPath(key));
      push('filename', fileNameWithoutExtension(key));
    }

    if (img.currentSrc) addPathCandidates(img.currentSrc);
    if (img.src) addPathCandidates(img.src);
    const srcset = img.getAttribute('srcset');
    if (srcset) {
      const parts = srcset.split(',');
      for (let i = 0; i < parts.length; i++) {
        const t = parts[i].trim().split(/\s+/)[0];
        if (t) addPathCandidates(t);
      }
    }
    return list;
  }

  function matchType(img) {
    const cands = candidatesFor(img);
    for (let i = 0; i < cands.length; i++) {
      const cand = cands[i];
      if (cand.kind === 'path' && manifest.has(cand.value)) return manifest.get(cand.value);
      if (cand.kind === 'filename') {
        const type = manifestByFilename.get(cand.value);
        if (type) return type;
      }
    }
    return null;
  }

  // Heuristic: if the parent is significantly larger than the image, use the
  // wrapper fallback so the badge is anchored to the image, not the block.
  function needsWrapper(img, parent) {
    if (!parent) return true;
    // Inline text-like containers should always be wrapped so the badge
    // doesn't detach from the image (typical case: <img> inside a <p>).
    const tag = parent.tagName;
    if (tag === 'P' || tag === 'LI' || tag === 'SPAN' || tag === 'STRONG' || tag === 'EM') {
      return true;
    }
    const imgRect = img.getBoundingClientRect();
    const pRect = parent.getBoundingClientRect();
    if (!imgRect.width || !pRect.width) return false;
    // If the parent is more than ~1.5x wider or taller than the image, wrap.
    return pRect.width > imgRect.width * 1.5 || pRect.height > imgRect.height * 1.5;
  }

  function injectBadge(img, type) {
    const badge = buildBadge(type);
    const parent = img.parentElement;
    if (!parent) return;

    if (needsWrapper(img, parent)) {
      // Fallback: wrap the image in an inline-block span.
      const wrap = document.createElement('span');
      wrap.className = 'ai-label-wrap';
      parent.insertBefore(wrap, img);
      wrap.appendChild(img);
      wrap.appendChild(badge);
      return;
    }

    // Default: badge as sibling of the image; ensure parent is positioned.
    const pos = window.getComputedStyle(parent).position;
    if (pos === 'static' || !pos) {
      parent.style.position = 'relative';
    }
    parent.appendChild(badge);
  }

  function processImage(img) {
    if (processed.has(img)) return;
    const type = matchType(img);
    if (!type) return;
    processed.add(img);
    injectBadge(img, type);
  }

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const imgs = scope.querySelectorAll('img');
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      processImage(img);
      // If the src wasn't populated yet (lazy loading), retry on load.
      if (!processed.has(img)) {
        img.addEventListener('load', function () {
          processImage(this);
        }, { once: true });
      }
    }
  }

  function start() {
    scan(document);
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        for (let j = 0; j < m.addedNodes.length; j++) {
          const node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            processImage(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
